import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  Building2,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const StakeholderManagement = ({ projectId, user }) => {
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    role: '',
    contact_email: '',
    contact_phone: '',
    influence: 'medium',
    interest: 'medium',
    communication_plan: ''
  });

  const canManage = user?.role === 'administrator' || user?.role === 'project_manager';
  const canEdit = user?.role === 'administrator' || user?.role === 'project_manager' || user?.role === 'social_actor';
  const canApprove = user?.role === 'administrator' || user?.role === 'project_manager';

  useEffect(() => {
    if (projectId) {
      fetchStakeholders();
    }
  }, [projectId]);

  const fetchStakeholders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/projects/${projectId}/stakeholders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStakeholders(response.data);
    } catch (error) {
      console.error('Failed to fetch stakeholders:', error);
      toast.error('Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      organization: '',
      role: '',
      contact_email: '',
      contact_phone: '',
      influence: 'medium',
      interest: 'medium',
      communication_plan: ''
    });
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/projects/${projectId}/stakeholders`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Stakeholder added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchStakeholders();
    } catch (error) {
      console.error('Failed to create stakeholder:', error);
      toast.error(error.response?.data?.detail || 'Failed to create stakeholder');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await axios.put(
        `${API}/api/projects/${projectId}/stakeholders/${selectedStakeholder.id}`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data?.status === 'pending_approval') {
        toast.success('Changes submitted for approval');
      } else {
        toast.success('Stakeholder updated successfully');
      }
      
      setShowEditDialog(false);
      resetForm();
      fetchStakeholders();
    } catch (error) {
      console.error('Failed to update stakeholder:', error);
      toast.error(error.response?.data?.detail || 'Failed to update stakeholder');
    }
  };

  const handleDelete = async (stakeholderId) => {
    if (!window.confirm('Are you sure you want to delete this stakeholder?')) return;
    
    try {
      await axios.delete(`${API}/api/projects/${projectId}/stakeholders/${stakeholderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Stakeholder deleted successfully');
      fetchStakeholders();
    } catch (error) {
      console.error('Failed to delete stakeholder:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete stakeholder');
    }
  };

  const handleApproval = async (approved) => {
    try {
      await axios.post(
        `${API}/api/projects/${projectId}/stakeholders/${selectedStakeholder.id}/approve-edit`,
        { approved, notes: approvalNotes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(approved ? 'Changes approved successfully' : 'Changes rejected');
      setShowApprovalDialog(false);
      setApprovalNotes('');
      fetchStakeholders();
    } catch (error) {
      console.error('Failed to process approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const openEditDialog = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setFormData({
      name: stakeholder.name || '',
      organization: stakeholder.organization || '',
      role: stakeholder.role || '',
      contact_email: stakeholder.contact_email || '',
      contact_phone: stakeholder.contact_phone || '',
      influence: stakeholder.influence || 'medium',
      interest: stakeholder.interest || 'medium',
      communication_plan: stakeholder.communication_plan || ''
    });
    setShowEditDialog(true);
  };

  const openApprovalDialog = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    setApprovalNotes('');
    setShowApprovalDialog(true);
  };

  const getInfluenceIcon = (level) => {
    switch (level) {
      case 'high': return <TrendingUp className="h-4 w-4 text-red-600" />;
      case 'medium': return <Minus className="h-4 w-4 text-yellow-700" />;
      case 'low': return <TrendingDown className="h-4 w-4 text-green-500" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getInterestIcon = (level) => {
    switch (level) {
      case 'high': return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'medium': return <Minus className="h-4 w-4 text-yellow-700" />;
      case 'low': return <TrendingDown className="h-4 w-4 text-gray-500" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (stakeholder) => {
    if (stakeholder.edit_status === 'pending_approval') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Pending Approval
        </Badge>
      );
    }
    if (stakeholder.approval_status === 'approved') {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Approved
        </Badge>
      );
    }
    if (stakeholder.approval_status === 'rejected') {
      return (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    }
    return null;
  };

  const getInfluenceInterestQuadrant = (influence, interest) => {
    if (influence === 'high' && interest === 'high') return { label: 'Manage Closely', color: 'bg-red-100 text-red-800' };
    if (influence === 'high' && (interest === 'medium' || interest === 'low')) return { label: 'Keep Satisfied', color: 'bg-orange-100 text-orange-800' };
    if ((influence === 'medium' || influence === 'low') && interest === 'high') return { label: 'Keep Informed', color: 'bg-blue-100 text-blue-800' };
    return { label: 'Monitor', color: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-purple-600" />
            Stakeholder Management
          </h2>
          <p className="text-gray-600 mt-1">Manage project stakeholders and their engagement</p>
        </div>
        
        {canManage && (
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Stakeholder
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Stakeholders</p>
                <p className="text-2xl font-bold">{stakeholders.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Influence</p>
                <p className="text-2xl font-bold">{stakeholders.filter(s => s.influence === 'high').length}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-600 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Interest</p>
                <p className="text-2xl font-bold">{stakeholders.filter(s => s.interest === 'high').length}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold">{stakeholders.filter(s => s.edit_status === 'pending_approval').length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-700 opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals Section */}
      {canApprove && stakeholders.filter(s => s.edit_status === 'pending_approval').length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              Pending Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stakeholders.filter(s => s.edit_status === 'pending_approval').map(stakeholder => (
                <div key={stakeholder.id} className="flex items-center justify-between bg-white p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">{stakeholder.name}</p>
                    <p className="text-sm text-gray-600">{stakeholder.organization} - {stakeholder.role}</p>
                    {stakeholder.pending_changes && (
                      <p className="text-xs text-yellow-700 mt-1">
                        Changes: {Object.keys(stakeholder.pending_changes).join(', ')}
                      </p>
                    )}
                  </div>
                  <Button onClick={() => openApprovalDialog(stakeholder)} size="sm">
                    Review Changes
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stakeholders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stakeholders.map(stakeholder => {
          const quadrant = getInfluenceInterestQuadrant(stakeholder.influence, stakeholder.interest);
          
          return (
            <Card key={stakeholder.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{stakeholder.name}</h3>
                      {getStatusBadge(stakeholder)}
                    </div>
                    <Badge variant="outline" className="mb-2">{stakeholder.role}</Badge>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span>{stakeholder.organization}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{stakeholder.contact_email}</span>
                  </div>
                  {stakeholder.contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{stakeholder.contact_phone}</span>
                    </div>
                  )}
                </div>
                
                {/* Influence/Interest Matrix */}
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="flex items-center gap-1 text-sm">
                      {getInfluenceIcon(stakeholder.influence)}
                      <span className="text-gray-600">Influence:</span>
                      <span className="font-medium capitalize">{stakeholder.influence}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      {getInterestIcon(stakeholder.interest)}
                      <span className="text-gray-600">Interest:</span>
                      <span className="font-medium capitalize">{stakeholder.interest}</span>
                    </div>
                  </div>
                  <Badge className={quadrant.color}>{quadrant.label}</Badge>
                </div>
                
                {/* Actions */}
                <div className="mt-4 pt-4 border-t flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setSelectedStakeholder(stakeholder); setShowDetailDialog(true); }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  
                  {canEdit && (
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(stakeholder)}>
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  
                  {canManage && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(stakeholder.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {stakeholders.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Shield className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No stakeholders yet</h3>
            <p className="text-gray-500 mt-1">Add your first stakeholder to start managing project relationships</p>
            {canManage && (
              <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add First Stakeholder
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={() => { setShowAddDialog(false); setShowEditDialog(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Stakeholder' : 'Add New Stakeholder'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Project Sponsor"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="organization">Organization *</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder="Organization name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+30 XXX XXX XXXX"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="influence">Influence Level *</Label>
                <Select value={formData.influence} onValueChange={(value) => setFormData({ ...formData, influence: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="interest">Interest Level *</Label>
                <Select value={formData.interest} onValueChange={(value) => setFormData({ ...formData, interest: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="communication_plan">Communication Plan</Label>
              <Textarea
                id="communication_plan"
                value={formData.communication_plan}
                onChange={(e) => setFormData({ ...formData, communication_plan: e.target.value })}
                placeholder="Describe how and when to communicate with this stakeholder..."
                rows={3}
              />
            </div>
            
            {user?.role === 'social_actor' && showEditDialog && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Your changes will be submitted for approval by a Project Manager or Administrator.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}>
              Cancel
            </Button>
            <Button onClick={showEditDialog ? handleUpdate : handleCreate}>
              {showEditDialog ? 'Save Changes' : 'Add Stakeholder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Stakeholder Details</DialogTitle>
          </DialogHeader>
          
          {selectedStakeholder && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold">{selectedStakeholder.name}</h3>
                {getStatusBadge(selectedStakeholder)}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Role</Label>
                  <p className="font-medium">{selectedStakeholder.role}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Organization</Label>
                  <p className="font-medium">{selectedStakeholder.organization}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{selectedStakeholder.contact_email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Phone</Label>
                  <p className="font-medium">{selectedStakeholder.contact_phone || 'N/A'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Influence</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getInfluenceIcon(selectedStakeholder.influence)}
                    <span className="font-medium capitalize">{selectedStakeholder.influence}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Interest</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {getInterestIcon(selectedStakeholder.interest)}
                    <span className="font-medium capitalize">{selectedStakeholder.interest}</span>
                  </div>
                </div>
              </div>
              
              {selectedStakeholder.communication_plan && (
                <div>
                  <Label className="text-gray-500">Communication Plan</Label>
                  <p className="mt-1 text-gray-700">{selectedStakeholder.communication_plan}</p>
                </div>
              )}
              
              {selectedStakeholder.approved_by && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    Approved by {selectedStakeholder.approved_by} on {new Date(selectedStakeholder.approved_at).toLocaleDateString('en-GB')}
                  </p>
                  {selectedStakeholder.approval_notes && (
                    <p className="text-sm text-green-700 mt-1">{selectedStakeholder.approval_notes}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Stakeholder Changes</DialogTitle>
          </DialogHeader>
          
          {selectedStakeholder && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Current: {selectedStakeholder.name}</h4>
                <p className="text-sm text-gray-600">{selectedStakeholder.organization} - {selectedStakeholder.role}</p>
              </div>
              
              {selectedStakeholder.pending_changes && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2 text-yellow-800">Proposed Changes:</h4>
                  <ul className="text-sm space-y-1">
                    {Object.entries(selectedStakeholder.pending_changes).map(([key, value]) => (
                      <li key={key} className="flex justify-between">
                        <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                        <span className="font-medium">{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div>
                <Label htmlFor="approval_notes">Notes (Optional)</Label>
                <Textarea
                  id="approval_notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add any notes about your decision..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleApproval(false)}>
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button onClick={() => handleApproval(true)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StakeholderManagement;
